import {CSSProperties} from 'aphrodite';

import {RetypeColumnType} from 'data-frames/header-menu/DataFrameHeaderMenuOptions';
import {LeafType, LeafTypeToFriendlyName} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {GRAY_LIGHTER} from 'ui-library/Colors';

// Keep in sync with benchling/data_frames/constants.py
export const DATA_FRAME_DISPLAY_ROW_LIMIT = 50000;

export const AnalysisToolPreferences = {
  PROJECT_ID: 'analysisTool.projectId',
  FOLDER_ID: 'analysisTool.folderId',
};

export const ANALYSIS_TOOL_SPACING = 12;

/** Shared styles for a main view component that has:
 * - A box shadow
 * - ANALYSIS_TOOL_SPACING margin
 * - Rounded borders
 * - Grows up to the height of the parent
 */
export const SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES = {
  borderRadius: 4,
  boxShadow: '0px 0px 4px rgb(92, 124, 131, 20%)',
  display: 'flex',
  flexDirection: 'column',
  margin: ANALYSIS_TOOL_SPACING,
  // Use maxHeight instead of height so that elements that are shorter than the height of the main view do not
  // grow taller than necessary.
  // Subtract out the vertical padding because a full 100% of the parent container would let the child grow
  // 2 * ANALYSIS_TOOL_SPACING too tall.
  maxHeight: `calc(100% - ${2 * ANALYSIS_TOOL_SPACING}px)`,
} satisfies CSSProperties;

export const ADD_ICON_WITH_HOVER_STYLES: CSSProperties = {
  marginLeft: 8,
  padding: 2,
  ':hover': {
    borderRadius: 4,
    background: GRAY_LIGHTER,
  },
};

/** List of step types that create Benchling data outside of the pipeline.
 *
 * Generated by `dev generate pipeline-frontend`. See `update_pipeline_frontend_constants.py`.
 */
export const ANALYSIS_OUTPUT_STEP_TYPES = [
  PipelineStepType.BULK_IMPORT,
  PipelineStepType.CREATE_ENTITIES,
  PipelineStepType.EXPORT_DATA_CATALOG_DATASET,
  PipelineStepType.EXPORT_DATA_CATALOG_FILE,
  PipelineStepType.INSERT_RESULT_URL_IN_DOCUMENT,
  PipelineStepType.LEGACY_DATA_IMPORT_ENTRY_CREATION,
  PipelineStepType.MOVE_INVENTORY_PLATES,
  PipelineStepType.NDC_CREATE_PROTEINS_FROM_DATASET,
  PipelineStepType.PUT_PLATES_IN_INVENTORY,
  PipelineStepType.SAVE_TO_RESULTS,
  PipelineStepType.SEND_TO_DOCUMENT,
  PipelineStepType.UPDATE_ENTITIES,
  PipelineStepType.UPDATE_INVENTORY_PLATES,
] as const;

/** List of step types that cannot be executed generically
 *
 * Generated by `dev generate pipeline-frontend`. See `update_pipeline_frontend_constants.py`.
 */
export const NON_EXECUTABLE_STEP_TYPES = [
  PipelineStepType.NOTEBOOK_TABLE_DATASET,
  PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION,
  PipelineStepType.PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION,
  PipelineStepType.PARAMETER_OPTIMIZATION_MODELING,
  PipelineStepType.PARAMETER_OPTIMIZATION_PREDICTION,
  PipelineStepType.PROCEDURE_RUN_DATA_FRAMES,
  PipelineStepType.TEMPLATE_INPUT,
  PipelineStepType.THIRD_PARTY,
] as const;

/**
 * Step types that cannot be refreshed by users.
 *
 * This includes step types that aren't supported by the step execution library (NON_EXECUTABLE_STEP_TYPES)
 * and step types that – while technically rerunnable – don't make sense to refresh, since their output data
 * wouldn't change.
 */
export const NON_REFRESHABLE_STEP_TYPES = [
  ...NON_EXECUTABLE_STEP_TYPES,
  PipelineStepType.DATA_CATALOG_DATASET,
  PipelineStepType.DATA_CATALOG_FILE,
  PipelineStepType.DATA_CATALOG_FILES,
  PipelineStepType.UPLOAD_CSV_DATASET,
] as const;

// Generated by `dev generate pipeline-frontend`. See `update_pipeline_frontend_constants.py`.
export const TRANSFORMATION_STEP_TYPES = [
  PipelineStepType.AGGREGATE_DATASET,
  PipelineStepType.APPLY_COLUMN_MAPPING,
  PipelineStepType.BIN_DATA,
  PipelineStepType.COMPUTED_COLUMN,
  PipelineStepType.FILTER_DATASET,
  PipelineStepType.FIND_AND_REPLACE,
  PipelineStepType.FLAG_ROWS,
  PipelineStepType.FORMAT_COLUMNS,
  PipelineStepType.HIDE_COLUMNS,
  PipelineStepType.MAP_COLUMNS,
  PipelineStepType.PIVOT_DATASET,
  PipelineStepType.REGRESSION_INTERPOLATION,
  PipelineStepType.RENAME_COLUMNS,
  PipelineStepType.REORDER_COLUMNS,
  PipelineStepType.RETYPE_COLUMNS,
  PipelineStepType.SORT_COLUMNS,
  PipelineStepType.UNPIVOT_DATASET,
  PipelineStepType.WINDOW_FUNCTIONS,
];

export const TYPES_TO_DISPLAY_NAMES: {[k in RetypeColumnType]: string} = {
  INT: LeafTypeToFriendlyName[LeafType.INTEGER],
  DOUBLE: LeafTypeToFriendlyName[LeafType.DECIMAL],
  STRING: LeafTypeToFriendlyName[LeafType.TEXT],
  BOOLEAN: LeafTypeToFriendlyName[LeafType.BOOLEAN],
};

// Keep in sync with AnalysisTemplateTable.MAX_DESCRIPTION_LENGTH
// in benchling/pipelines/pipeline_templates/persistence/analysis_template_table.py
export const MAX_AUTOMATION_TEMPLATE_DESCRIPTION_LENGTH = 1500;
